import { parseExpiresAt, rewriteSignedUrlForProxy } from "./bimSignedDownloadUrl";
import { procoreClient } from "./client";
import { parseFlexibleResponse } from "./utils";

type SignedDownloadUrlItem = { signed_download_url?: string | null };
type SignedDownloadUrlsResponse = SignedDownloadUrlItem[] | { data?: SignedDownloadUrlItem[] };

type BimSceneFileSignedDownloadUrlParams = {
    companyId: number;
    projectId: number;
    sceneId: string;
    /** Scene version the sheet was resolved from; the endpoint also accepts the `current` alias. */
    sceneVersionId: string;
    /** Scene-file placement id (`platform_file_instance_id`). */
    fileInstanceId: string;
};

type CacheEntry = {
    /** In-flight or settled promise. Settled entries are reused until the URL nears expiry. */
    promise: Promise<string | null>;
    /** Unix-epoch seconds the URL stops being usable; `undefined` while in flight. */
    expiresAt?: number;
};

/** Refresh this many seconds before `expiresAt` so an in-flight fetch still completes on a fresh URL. */
const REFRESH_MARGIN_SECONDS = 60;

const cache = new Map<string, CacheEntry>();

function getCacheKey(p: BimSceneFileSignedDownloadUrlParams): string {
    return `${p.companyId}:${p.projectId}:${p.sceneId}:${p.sceneVersionId}:${p.fileInstanceId}`;
}

/**
 * Signed download URL for the resource of a file placed in a scene version —
 * the prefix the mini map tile set (manifest, previews, tiles) is addressed
 * under (BM-13597, RFD00143 option C).
 *
 * The Platform endpoint exists (`bim-platform-service`
 * `SceneFileController.ListSceneVersionFileSignedDownloadUrls`); `fileInstanceId`
 * is the scene-file placement id, which Model Manager stores as
 * `platform_file_instance_id` and still needs to expose in `plans/extended`.
 * Mocked in `mock/signed-download-urls.mock.ts`.
 *
 * Cached + deduped per placement id and refreshed as the URL nears expiry
 * (same pattern as `createBimSignedDownloadUrl`), so tile/preview fetches in
 * sessions outliving one signature keep resolving a usable prefix. Pass
 * `forceRefresh` after a 401/403 to drop the cached entry and re-mint.
 *
 * Returns `null` when unavailable (no successful import yet → empty array,
 * 404/422, request failure) — the caller stays on the PDF route. `null`
 * results are not cached: the import may complete at any moment.
 */
export async function createBimSceneFileSignedDownloadUrl(
    params: BimSceneFileSignedDownloadUrlParams,
    options?: { forceRefresh?: boolean },
): Promise<string | null> {
    const cacheKey = getCacheKey(params);
    const nowSeconds = Math.floor(Date.now() / 1000);

    const cached = cache.get(cacheKey);
    if (cached && !options?.forceRefresh && isEntryFresh(cached, nowSeconds)) {
        return cached.promise;
    }

    const promise = fetchSignedDownloadUrl(params);
    const entry: CacheEntry = { promise };
    cache.set(cacheKey, entry);

    void promise.then((signedUrl) => {
        // Only mutate the cache if it still points at this in-flight entry.
        if (cache.get(cacheKey) !== entry) {
            return;
        }
        const expiresAt = signedUrl ? parseExpiresAt(signedUrl) : undefined;
        if (expiresAt === undefined) {
            cache.delete(cacheKey);
            return;
        }
        entry.expiresAt = expiresAt;
    });

    return promise;
}

function isEntryFresh(entry: CacheEntry, nowSeconds: number): boolean {
    // In-flight requests have no `expiresAt` yet — reuse them so concurrent callers dedupe.
    if (entry.expiresAt === undefined) {
        return true;
    }
    return entry.expiresAt - REFRESH_MARGIN_SECONDS > nowSeconds;
}

async function fetchSignedDownloadUrl(params: BimSceneFileSignedDownloadUrlParams): Promise<string | null> {
    try {
        const raw = await procoreClient<string>(
            `/rest/v2.0/companies/${params.companyId}/projects/${params.projectId}/bim/scenes/${params.sceneId}/versions/${params.sceneVersionId}/files/${params.fileInstanceId}/signed_download_urls`,
            { parseResponse: async (res) => res.text() },
        );

        const parsed = parseFlexibleResponse<SignedDownloadUrlsResponse>(raw);
        const items = Array.isArray(parsed) ? parsed : typeof parsed === "object" && parsed ? (parsed.data ?? []) : [];
        const signedUrl = items[0]?.signed_download_url;
        return typeof signedUrl === "string" && signedUrl ? rewriteSignedUrlForProxy(signedUrl) : null;
    } catch {
        return null;
    }
}

/** Test/debug helper: drop all cached signed URLs. */
export function clearBimSceneFileSignedDownloadUrlCache(): void {
    cache.clear();
}
